import { Link2, Search, Sparkles, CircleCheckBig } from "lucide-react";
import { Link } from "react-router";
import type { BrainCategoryShelf } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import { DISPLAY_LOCALE } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * What the Ask tab shows before anything has been asked.
 *
 * It replaces a card of suggested questions. Those demonstrated what the Brain
 * *can* answer; this says what it has been given to answer *from* — which is
 * the question a first-time reader actually has, and the one that predicts
 * whether their first question will work.
 *
 * **One line under the box, and the cards only when there are no files** (the
 * owner, 2026-09-27, `product/0013`). With files, the two cards were furniture
 * every time the tab was opened: the line says what the Brain answers from,
 * and links to where it is. With no files at all, the cards say how to give it
 * some.
 */

/** What the line counts. */
export type AnswersFromCounts = {
  /**
   * The files the Brain may read: stored, not hidden, not an identical copy,
   * and not in an import still being read. The Brain summary's
   * `bySourceType["project-doc"]`, the number the Sources tab counts from.
   */
  files: number;
  /** Every file the project holds, hidden and still being read included. */
  filesHeld: number;
  /** The seeded manual sections: the summary's `bySourceType.manual`. */
  manualSections: number;
  /** Notes written straight into the Brain: the summary's `bySourceType.upload`. */
  notes: number;
  /** Every decision. Each one is in the Brain as one row. */
  decisions: number;
};

/** "1 file", "1,204 files": the reader's digits, the interface's words. */
function counted(n: number, one: string, many: string): string {
  return `${new Intl.NumberFormat(DISPLAY_LOCALE).format(n)} ${n === 1 ? one : many}`;
}

/** "a", "a and b", "a, b and c". No comma before "and", as British English writes it. */
function listed(parts: readonly string[]): string {
  if (parts.length < 2) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * What the Brain answers from, in one line: "Answers from 64 files, the
 * journal and 12 decisions".
 *
 * **Code writes it**, in British English, the interface language
 * (`architecture/0032` §2), with the reader's digits.
 *
 * **The journal is named, not counted.** Every question reads the whole
 * journal, so it is always one of the things answered from. The dashboard has
 * no cheap count of it: the Brain summary counts only the seeded journal rows,
 * and the journal's own list is every entry in full.
 *
 * **No files says so**, rather than "0 files". A project whose files are all
 * hidden, or still being read, has files the Brain cannot read yet. That is a
 * different thing from having none.
 *
 * **Every kind the summary counts that the Brain reads**, when there are any:
 * files, manual sections, notes and decisions, with the journal named. The
 * line reads as the whole list, so a kind left out read as a kind not read:
 * a project with no files and ten notes said "answers from the journal". Two
 * things are still not named. The crew is counted from the roster, not the
 * summary, so naming it would cost another read. And the summary's `other`
 * is the fallback for a row it cannot parse, which has no name to give.
 */
export function answersFromLine({
  files,
  filesHeld,
  manualSections,
  notes,
  decisions,
}: AnswersFromCounts): string {
  const rest = [
    "the journal",
    ...(manualSections > 0 ? [counted(manualSections, "manual section", "manual sections")] : []),
    ...(notes > 0 ? [counted(notes, "note", "notes")] : []),
    ...(decisions > 0 ? [counted(decisions, "decision", "decisions")] : []),
  ];
  if (files > 0) return `Answers from ${listed([counted(files, "file", "files"), ...rest])}`;
  const none = filesHeld > 0 ? "No files the Brain can read yet" : "No files yet";
  return `${none} — answers from ${listed(rest)}`;
}

/**
 * The line under the ask box, linking to Sources. Nothing until the counts are
 * known: "No files yet" for as long as a request takes would be a claim
 * nobody made.
 */
export function AnswersFrom({
  projectId,
  counts,
}: {
  projectId: string;
  counts: AnswersFromCounts | null;
}) {
  if (!counts) return null;
  return (
    <p className="mt-3 text-center text-[0.84rem] text-muted">
      <Link
        to={`/projects/${projectId}/brain/sources`}
        className="underline-offset-2 transition-colors hover:text-ink hover:underline"
      >
        {answersFromLine(counts)}
      </Link>
    </p>
  );
}

type Step = {
  n: number;
  title: string;
  body: string;
  icon: typeof Link2;
  tone: string;
  /** Set when the step names a place, which then becomes a link. */
  to?: string;
};

const STEPS: readonly Step[] = [
  {
    n: 1,
    title: "Connect a source",
    body: "Link an inbox, chat or vendor portal — or drop in documents.",
    icon: Link2,
    tone: "bg-brand-tint-3 text-brand",
    /** Step 1 is the only one that is also an action. */
    to: "sources",
  },
  {
    n: 2,
    title: "Ingest & index",
    body: "Every item is read, dated and linked to people, equipment & sectors.",
    icon: Sparkles,
    tone: "bg-subtle text-muted",
  },
  {
    n: 3,
    title: "Ask with citations",
    body: "Get one answer, traced back to the exact source it came from.",
    icon: Search,
    tone: "bg-ok-bg text-ok",
  },
  {
    n: 4,
    title: "Decide & approve",
    body: "Approve the answer as it stands — or explain how and why you decided, then approve.",
    icon: CircleCheckBig,
    tone: "bg-warn-bg text-warn",
  },
];

/** How the Brain comes to answer, in four steps. Shown only when the project has no files. */
export function HowTheBrainLearns({ projectId }: { projectId: string }) {
  return (
    <section className="rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-5">
      <h2 className="mb-4 font-display text-[0.66rem] font-semibold tracking-[0.12em] text-muted uppercase">
        How the Brain learns
      </h2>
      <ol className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step) => {
          const Icon = step.icon;
          return (
            <li key={step.n} className="flex gap-3">
              <span
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-tile",
                  step.tone,
                )}
              >
                <Icon size={17} aria-hidden />
              </span>
              <div className="min-w-0">
                <h3 className="font-display text-[0.95rem] font-semibold text-ink">
                  {step.n} · {step.title}
                </h3>
                <p className="mt-1 text-[0.86rem] leading-relaxed text-muted">
                  {/* The first step names a place, so it links to it. A step
                      that says "navigate to Sources" and cannot is a caption. */}
                  {step.to ? (
                    <>
                      <Link
                        to={`/projects/${projectId}/brain/${step.to}`}
                        className="font-semibold text-brand underline-offset-2 hover:underline"
                      >
                        Go to Sources
                      </Link>
                      {" — "}
                      {step.body}
                    </>
                  ) : (
                    step.body
                  )}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * Where the project's files will be sorted, when it has none yet: the
 * tenant's categories (`document-ai/0032`, 2026-10-03).
 *
 * Until then it ticked six common areas from the project's outline. With no
 * files every tick was empty, so the ticks said nothing. It is now the list a
 * file goes into, in the tenant's order and words, from the Brain summary the
 * screen already reads: the same names the Sources tab's shelves carry.
 *
 * **Shown only when the project has no files** (2026-09-27, `product/0013`).
 * With files it used to read "These are the documents already available"; the
 * line under the ask box says that now.
 */
export function DocumentsAvailable({
  projectId,
  categories,
}: {
  projectId: string;
  /** The Brain summary's `categories`, in the tenant's order. */
  categories: readonly Pick<BrainCategoryShelf, "key" | "name">[] | undefined;
}) {
  // Nothing at all until it is known, rather than a card that fills in.
  if (!categories) return null;

  return (
    <section className="rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-5">
      <h2 className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-muted uppercase">
        No documents yet
      </h2>
      <p className="mt-1.5 mb-4 text-[0.88rem] text-muted">
        The Brain answers from what it has been given. Nothing has been uploaded to this project yet.
        {categories.length > 0 && " Each file is sorted into one of these categories:"}
      </p>

      {categories.length > 0 && (
        <ul aria-label="Where files are sorted" className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <li key={category.key} className="font-display text-[0.92rem] font-semibold text-ink-slate">
              {category.name}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 border-t border-border pt-4">
        <Button asChild>
          <Link to={`/projects/${projectId}/brain/sources`}>
            <Link2 size={15} aria-hidden />
            Connect a source
          </Link>
        </Button>
      </div>
    </section>
  );
}
